import React, {createRef, ReactElement, useEffect, useState} from 'react';
import {CameraRefs, ImageEditorImage, ImageEditorImageType, StyledCamera} from "@/shared/StyledCamera";
import {FileUtil} from "@/shared/file_util";
import {CircularProgress, Typography} from "@mui/material";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE, DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN} from "../shared/BaseApp";
import {DrawingUtils, FaceLandmarker, NormalizedLandmark} from "@mediapipe/tasks-vision";
import {PD_LG, PD_XLG2} from "../shared/dimens";
import {translucentBlack, white} from "../shared/colors";
import {$KTM, KeyMetadatas} from "../shared/types";
import {Rect} from "./types";

type Connection = {
  start: number;
  end: number;
}

export enum CameraMode {
  CAPTURE,
  PREVIEW,
}

function CameraView(props: {
  mode?: CameraMode,
  onDone?: (image: ImageEditorImage) => void,
  imageUrl?: string,
  setImageUrl?: (url: string) => void,
  videoReadyCallback?: (refs: CameraRefs) => void,
  videoDestroyCallback?: () => void,
}): ReactElement {
  const [initialImage, setInitialImage] = useState<ImageEditorImage>();
  const value: string = props.imageUrl;
  const mode: CameraMode = props.mode;
  useEffect(() => {
    if (mode === CameraMode.PREVIEW && (value?.startsWith("blob:") || value?.startsWith("http:") || value?.startsWith("https:"))) {
      (async function () {
        try {
          const blob = await fetch(value).then(res => res.blob());
          const dataUrl = await FileUtil.blobToDataUrl(blob);
          if (dataUrl.startsWith("data:image/")) {
            setInitialImage({
              data: dataUrl,
              type: ImageEditorImageType.BASE64URL,
              cropped: true,
            });
          } else {
            setInitialImage(null);
          }
        } catch (e) {
          setInitialImage(null);
        }
      })();
    } else {
      setInitialImage(null);
    }
  }, [mode, value]);
  return initialImage !== undefined
    ? ((mode === undefined || mode === CameraMode.CAPTURE) || initialImage !== null
      ? <StyledCamera
        imageOptions={{
          keepAspectRatio: window.innerWidth / window.innerHeight,
        }}
        facingMode={"user"}
        videoReadyCallback={props.videoReadyCallback}
        videoDestroyCallback={props.videoDestroyCallback}
        initialImage={initialImage}
        autoUpload={false}
        onUploadDone={(urls: string[]) => {
          props.setImageUrl(urls[0]);
        }}
        onDone={image => props.onDone?.(image)}
      />
      : <Typography>Unable to load image</Typography>)
    : <CircularProgress/>;
}

export class CameraPredictionFrame {

  readonly faceOval: NormalizedLandmark[] = [];
  readonly lipsOuter: NormalizedLandmark[] = [];
  readonly lipsInner: NormalizedLandmark[] = [];
}

class CameraPrediction {

  private lastVideoTime = -1;
  private readonly drawingUtils;
  private results = undefined;
  private rangeX: number = 1;
  private rangeY: number = 1;

  constructor(readonly cameraRefs: CameraRefs, readonly faceLandmarker: FaceLandmarker, readonly callback: (frame: CameraPredictionFrame) => void) {
    this.drawingUtils = new DrawingUtils(cameraRefs.overlay.current.getContext("2d"));
  }

  async predict() {
    if (this.stopped) {
      return;
    }
    const drawingUtils = this.drawingUtils;
    const faceLandmarker = this.faceLandmarker;
    const canvasElement = this.cameraRefs.overlay.current;
    const video = this.cameraRefs.player.current;
    // Now let's start detecting the stream.
    let startTimeMs = performance.now();
    if (video && this.lastVideoTime !== video.currentTime) {
      const videoScale = Math.max(window.innerWidth / video.videoWidth, window.innerHeight / video.videoHeight);
      this.rangeX = window.innerWidth / (video.videoWidth * videoScale);
      this.rangeY = window.innerHeight / (video.videoHeight * videoScale);
      canvasElement.width = window.innerWidth;
      canvasElement.height = window.innerHeight;
      this.lastVideoTime = video.currentTime;
      this.results = faceLandmarker.detectForVideo(video, startTimeMs);
    }
    if (this.results?.faceLandmarks) {
      for (let landmarks of [...this.results.faceLandmarks as NormalizedLandmark[][]]) {
        // Transform from video-normalized space into overlay-normalized space.
        landmarks = landmarks.map(landmark => ({
          x: 0.5 + (landmark.x - 0.5) / this.rangeX,
          y: 0.5 + (landmark.y - 0.5) / this.rangeY,
          z: landmark.z,
          visibility: landmark.visibility,
        }));
        drawingUtils.drawConnectors(
          landmarks,
          FaceLandmarker.FACE_LANDMARKS_FACE_OVAL,
          {color: "#E0E0E0"}
        );
        drawingUtils.drawConnectors(
          landmarks,
          FaceLandmarker.FACE_LANDMARKS_LIPS,
          {color: "#E0E0E0"}
        );
        const frame = new CameraPredictionFrame();
        let index: number = 0;
        let connections: Connection[];
        if ((connections = FaceLandmarker.FACE_LANDMARKS_FACE_OVAL)?.length > 0) {
          frame.faceOval.push(landmarks[connections[index].start]);
          while (++index < connections.length) {
            const current = connections[index];
            if (connections[index - 1].end !== current.start) {
              break;
            }
            frame.faceOval.push(landmarks[current.start]);
          }
          frame.faceOval.push(landmarks[connections[index - 1].end]);
        }
        index = 0;
        if ((connections = FaceLandmarker.FACE_LANDMARKS_LIPS)?.length > 0) {
          for (let i =0; i < 2; i++) {
            frame.lipsOuter.push(landmarks[connections[index].start]);
            while (++index < connections.length) {
              const current = connections[index];
              if (connections[index - 1].end !== current.start) {
                break;
              }
              frame.lipsOuter.push(landmarks[current.start]);
            }
            frame.lipsOuter.push(landmarks[connections[index - 1].end]);
          }
          for (let i =0; i < 2; i++) {
            frame.lipsInner.push(landmarks[connections[index].start]);
            while (++index < connections.length) {
              const current = connections[index];
              if (connections[index - 1].end !== current.start) {
                break;
              }
              frame.lipsInner.push(landmarks[current.start]);
            }
            frame.lipsInner.push(landmarks[connections[index - 1].end]);
          }
        }
        this.callback(frame);
      }
    }
    window.requestAnimationFrame(() => this.predict());
  }

  private stopped: boolean;

  stop() {
    this.stopped = true;
  }
}

enum FacePosition {
  NONE = "none",
  FAR = "far",
  CLOSE = "close",
  NO_SMILE = "no_smile",
  IDEAL = "ideal",
}

type FacePositionMetadata = {
  text: string,
  color: string,
}

const FACE_POSITION_KTM = new KeyMetadatas<FacePositionMetadata>([
  $KTM(FacePosition.NONE, {text: "", color: "white"}),
  $KTM(FacePosition.FAR, {text: "Too far", color: "red"}),
  $KTM(FacePosition.CLOSE, {text: "Too close", color: "red"}),
  $KTM(FacePosition.NO_SMILE, {text: "Smile!", color: "yellow"}),
  $KTM(FacePosition.IDEAL, {text: "GREAT! Take a Pic", color: "green"}),
]);


export class Camera {

  private readonly messageRef = createRef<HTMLDivElement>();

  private frame: CameraPredictionFrame;
  private lastFacePosition: FacePosition = FacePosition.NONE;
  private prediction: CameraPrediction;

  constructor(readonly landmarker: FaceLandmarker) {
  }

  private calcFacePosition(frame: CameraPredictionFrame): FacePosition {
    this.frame = frame;
    const faceRect = new Rect();
    for (const pos of frame.faceOval) {
      faceRect.add(pos.x, pos.y);
    }
    const faceHeight = faceRect.height();
    if (faceHeight < 0.5) {
      return FacePosition.FAR;
    } else if (faceHeight > 0.6) {
      return FacePosition.CLOSE;
    }
    const lipsOuterRect = new Rect();
    for (const pos of frame.lipsOuter) {
      lipsOuterRect.add(pos.x, pos.y);
    }
    const lipsOuterHeight = lipsOuterRect.height();
    // const lipsInnerRect = new Rect();
    // for (const pos of frame.lipsInner) {
    //   lipsInnerRect.add(pos.x, pos.y);
    // }
    // const lipsInnerHeight = lipsInnerRect.height();
    // console.log(lipsOuterHeight + ", " + lipsInnerHeight);
    if (lipsOuterHeight < 0.07) {
      return FacePosition.NO_SMILE;
    }
    return FacePosition.IDEAL;
  }

  show(onDone: (image: ImageEditorImage, frame: CameraPredictionFrame) => void): void {
    const id = BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN},
      () => <div style={{position: "relative", background: "black", width: "100dvw", height: "100dvh"}}>
        <CameraView
          onDone={image => {
            BaseApp.CONTEXT.hideDialog(id);
            setTimeout(() => {
              onDone(image, this.frame);
            }, 1);
          }}
          videoReadyCallback={refs => {
            this.prediction = new CameraPrediction(refs, this.landmarker, frame => {
              const facePosition = this.calcFacePosition(frame);
              if (this.lastFacePosition !== facePosition) {
                const metadata = FACE_POSITION_KTM.findMetadata(facePosition);
                this.messageRef.current.innerText = metadata.text;
                this.messageRef.current.style.color = metadata.color;
              }
              this.lastFacePosition = facePosition;
            });
            this.prediction.predict();
          }}
          videoDestroyCallback={() => {
            this.prediction?.stop();
          }}
        />
        <div
          style={{
            position: "absolute",
            display: "flex",
            top: 0,
            left: 0,
            right: 0,
            height: 72,
            padding: `${PD_LG}px ${PD_XLG2}px`,
            background: translucentBlack,
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Typography
            variant={"h5"}
            component="div"
            ref={this.messageRef}
            style={{
              color: white,
            }}/>
        </div>
      </div>);
  }
}